import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, User as UserIcon, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import PatientDialog from "@/components/PatientDialog";

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [query, setQuery] = useState("");
  const [show, setShow] = useState(false);

  const load = async () => {
    const { data } = await api.get("/patients");
    setPatients(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = patients.filter((p) =>
    p.name.toLowerCase().includes(query.toLowerCase()) ||
    (p.email || "").toLowerCase().includes(query.toLowerCase()) ||
    (p.phone || "").includes(query)
  );

  return (
    <div className="p-8 lg:p-12">
      <header className="flex items-end justify-between mb-8">
        <div>
          <span className="label-eyebrow text-[#B5704A]">Personas</span>
          <h1 className="font-display text-3xl sm:text-4xl font-[350] text-[#3A2418] mt-2">
            Tus <span className="italic font-[700]">clientas</span>
          </h1>
          <p className="text-[#6F5A4C] mt-2 text-sm">{patients.length} clientas en total</p>
        </div>
        <Button onClick={() => setShow(true)} className="bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full" data-testid="new-patient-btn">
          <Plus className="w-4 h-4 mr-2" /> Nueva clienta
        </Button>
      </header>

      <div className="relative max-w-md mb-6">
        <Search className="absolute left-3 top-3 w-4 h-4 text-[#6F5A4C]" />
        <Input
          placeholder="Buscar clientas..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9 bg-white border-[#E8DFD2]"
          data-testid="search-patients"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="surface-card p-12 text-center">
          <UserIcon className="w-12 h-12 mx-auto mb-4 text-[#6F5A4C] opacity-40" />
          <h3 className="font-display text-xl text-[#3A2418]">Sin clientas aún</h3>
          <p className="text-sm text-[#6F5A4C] mt-2">Crea tu primera clienta para comenzar a registrar tratamientos y anamnesis.</p>
          <Button onClick={() => setShow(true)} className="mt-6 bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full">
            <Plus className="w-4 h-4 mr-2" /> Agregar clienta
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <Link
              key={p.id}
              to={`/app/pacientes/${p.id}`}
              className="surface-card surface-card-hover p-5 block"
              data-testid={`patient-card-${p.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#E8DFD2] flex items-center justify-center font-display font-[700] text-[#3A2418]">
                  {p.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-[#3A2418] truncate">{p.name}</h3>
                  {p.gender && <div className="text-xs text-[#6F5A4C]">{p.gender}</div>}
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-[#6F5A4C]">
                {p.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {p.email}</div>}
                {p.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {p.phone}</div>}
              </div>
            </Link>
          ))}
        </div>
      )}

      <PatientDialog open={show} onClose={() => setShow(false)} onSaved={load} />
    </div>
  );
}
